import { z } from "zod";

/**
 * An endpoint contract: everything the client and the API must agree on.
 * The API pass turns each of these into a Hono OpenAPI route with the same
 * path, method and schemas, so this file is the spec until the API exists.
 */
export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export interface Endpoint<
  P extends z.ZodType = z.ZodType,
  Q extends z.ZodType = z.ZodType,
  B extends z.ZodType = z.ZodType,
  R extends z.ZodType = z.ZodType,
> {
  id: string;
  method: HttpMethod;
  /** Express-style path, e.g. "/companies/:companyId/invoices". */
  path: string;
  params?: P;
  query?: Q;
  body?: B;
  response: R;
  /** false for public endpoints (login, sign-up, public signature pages). */
  auth?: boolean;
}

export function endpoint<
  P extends z.ZodType = z.ZodNever,
  Q extends z.ZodType = z.ZodNever,
  B extends z.ZodType = z.ZodNever,
  R extends z.ZodType = z.ZodType,
>(def: Endpoint<P, Q, B, R>): Endpoint<P, Q, B, R> {
  return { auth: true, ...def };
}

type Field<K extends string, S> = [S] extends [z.ZodNever] ? object : S extends z.ZodType ? { [k in K]: z.input<S> } : object;

/** What a caller passes: only the parts the endpoint declares. */
export type EndpointInput<E> = E extends Endpoint<infer P, infer Q, infer B, z.ZodType>
  ? Field<"params", P> & Field<"query", Q> & Field<"body", B>
  : never;

export type EndpointOutput<E> = E extends Endpoint<z.ZodType, z.ZodType, z.ZodType, infer R> ? z.output<R> : never;

/** Fill ":name" segments from params. */
export function buildPath(path: string, params?: Record<string, unknown>): string {
  return path.replace(/:([A-Za-z_]+)/g, (_, key: string) => {
    const v = params?.[key];
    if (v === undefined || v === null) throw new Error(`Missing path param "${key}" for ${path}`);
    return encodeURIComponent(String(v));
  });
}
